import * as React from "react";
import { format } from "date-fns";
import {
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Loader2,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PdfViewer } from "@/components/PdfViewer";

const IMAGE_EXTS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "bmp",
  "heic",
  "heif",
];

const getExtension = (source: string): string => {
  if (!source) return "";
  const clean = source.split("?")[0].split("#")[0];
  const ext = clean.split(".").pop()?.toLowerCase() ?? "";
  // Guard against paths with no extension at all (last segment had no dot)
  if (ext === clean.toLowerCase()) return "";
  return ext;
};

const matchesExt = (sources: string[], exts: string[]): boolean =>
  sources.some((s) => exts.includes(getExtension(s)));

const isImageFile = (filename: string, fileUrl?: string): boolean =>
  matchesExt(fileUrl ? [filename, fileUrl] : [filename], IMAGE_EXTS);

const isPdfFile = (filename: string, fileUrl?: string): boolean =>
  matchesExt(fileUrl ? [filename, fileUrl] : [filename], ["pdf"]);

interface FilePreviewContentProps {
  fileUrl: string;
  fileName: string;
  className?: string;
  onLoad?: () => void;
}

export const FilePreviewContent = ({
  fileUrl,
  fileName,
  className,
  onLoad,
}: FilePreviewContentProps) => {
  const isImage = isImageFile(fileName, fileUrl);
  const isPdf = isPdfFile(fileName, fileUrl);
  const [isLoading, setIsLoading] = React.useState(!isPdf);

  React.useEffect(() => {
    setIsLoading(!isPdf);
  }, [fileUrl, isPdf]);

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  return (
    <div
      className={cn(
        "relative flex flex-1 items-center justify-center overflow-auto bg-muted min-h-0",
        className
      )}
    >
      {isLoading && !isPdf && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <span className="z-body text-muted-foreground">
              Loading preview…
            </span>
          </div>
        </div>
      )}

      {isImage ? (
        <div className="flex h-full w-full items-center justify-center overflow-auto p-6">
          <img
            src={fileUrl}
            alt={fileName}
            className="max-h-full max-w-full select-none rounded-lg object-contain shadow-sm"
            onLoad={handleLoad}
            onError={handleLoad}
            draggable={false}
            style={{ display: isLoading ? "none" : "block" }}
          />
        </div>
      ) : isPdf ? (
        <div className="flex h-full w-full flex-col">
          <PdfViewer url={fileUrl} onLoadComplete={onLoad} />
        </div>
      ) : (
        <iframe
          src={fileUrl}
          className="h-full w-full flex-1 border-0 bg-muted"
          title={fileName || "Document preview"}
          onLoad={handleLoad}
        />
      )}
    </div>
  );
};

interface FilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileUrl: string;
  fileName: string;
  /** Overrides the fileName as the visible title (e.g. "Treatment Document"). */
  title?: string;
  /** Optional "Uploaded At" date shown under the filename. */
  createdAt?: Date | string | number | null;
  /** Extra row rendered below the filename (e.g. doc-type Select + date Popover). */
  headerExtra?: React.ReactNode;
  /** Extra buttons rendered before the Open-in-new-tab + Close buttons. */
  headerActions?: React.ReactNode;
  /** Whether to show the Open-in-new-tab button. Defaults to true. */
  showOpenInNewTab?: boolean;
  onPointerDownOutside?: React.ComponentProps<
    typeof DialogContent
  >["onPointerDownOutside"];
  onInteractOutside?: React.ComponentProps<
    typeof DialogContent
  >["onInteractOutside"];
}

export const FilePreviewDialog = ({
  open,
  onOpenChange,
  fileUrl,
  fileName,
  title,
  createdAt,
  headerExtra,
  headerActions,
  showOpenInNewTab = true,
  onPointerDownOutside,
  onInteractOutside,
}: FilePreviewDialogProps) => {
  const displayTitle = title ?? fileName ?? "Untitled";
  const isImage = isImageFile(fileName, fileUrl);
  const createdAtDate =
    createdAt == null
      ? null
      : createdAt instanceof Date
        ? createdAt
        : new Date(createdAt);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[95vh] max-h-[95vh] w-[95vw] sm:max-w-[95vw] flex-col gap-0 overflow-hidden rounded-3xl border border-border/60 bg-card p-0 shadow-sm [&>button]:hidden"
        onPointerDownOutside={onPointerDownOutside}
        onInteractOutside={onInteractOutside}
      >
        <DialogTitle className="sr-only">{displayTitle}</DialogTitle>

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-card px-5 py-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              {isImage ? (
                <ImageIcon className="h-[18px] w-[18px] text-primary" />
              ) : (
                <FileText className="h-[18px] w-[18px] text-primary" />
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <span
                className="z-h3 max-w-[600px] truncate text-foreground"
                title={displayTitle}
              >
                {displayTitle}
                {headerExtra && createdAtDate && (
                  <span className="z-caption px-4 text-muted-foreground">
                    Uploaded At: {format(createdAtDate, "dd MMM yyyy")}
                  </span>
                )}
              </span>

              {!headerExtra && createdAtDate && (
                <span className="z-caption text-muted-foreground">
                  Uploaded At: {format(createdAtDate, "dd MMM yyyy")}
                </span>
              )}

              {headerExtra}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {headerActions}
            {showOpenInNewTab && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.open(fileUrl, "_blank")}
                className="h-9 w-9 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Open in new tab"
              >
                <ExternalLink className="h-5 w-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-9 w-9 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <FilePreviewContent fileUrl={fileUrl} fileName={fileName} />
      </DialogContent>
    </Dialog>
  );
};
